/**
 * Partner Companies API Service
 * HTTP client methods for partner company management endpoints
 * Feature: 028-partner-companies (T026 - US1)
 */

import { apiClient } from '@/core/api/client';
import type {
  PartnerCompany,
  PartnerCompanyWithCounts,
  PartnerCompanyOption,
  PaginatedResponse,
  CreatePartnerCompanyPayload,
  UpdatePartnerCompanyPayload,
  PartnerCompanyListParams,
  PartnerCompanySearchParams,
  AssignedUser,
  UserAssignmentResult,
  PartnerCompanyUserListParams,
} from '../types/partner-company.types';

const BASE_PATH = '/admin/partner-companies';

/**
 * List partner companies with pagination and filters
 * GET /admin/partner-companies
 *
 * @param params - Query parameters (page, limit, search, isActive)
 * @returns Paginated list of partner companies
 */
export async function getPartnerCompanies(
  params?: PartnerCompanyListParams,
): Promise<PaginatedResponse<PartnerCompany>> {
  const response = await apiClient.get<PaginatedResponse<PartnerCompany>>(
    BASE_PATH,
    { params },
  );
  return response.data;
}

/**
 * Create a new partner company
 * POST /admin/partner-companies
 *
 * @param payload - Partner company data
 * @returns Created partner company
 */
export async function createPartnerCompany(
  payload: CreatePartnerCompanyPayload,
): Promise<PartnerCompany> {
  const response = await apiClient.post<PartnerCompany>(BASE_PATH, payload);
  return response.data;
}

/**
 * Get partner company by ID with user counts
 * GET /admin/partner-companies/:id
 *
 * @param id - Partner company ID
 * @returns Partner company with user counts
 */
export async function getPartnerCompany(
  id: string,
): Promise<PartnerCompanyWithCounts> {
  const response = await apiClient.get<PartnerCompanyWithCounts>(
    `${BASE_PATH}/${id}`,
  );
  return response.data;
}

/**
 * Update partner company
 * PATCH /admin/partner-companies/:id
 *
 * @param id - Partner company ID
 * @param payload - Partial partner company data to update
 * @returns Updated partner company
 */
export async function updatePartnerCompany(
  id: string,
  payload: UpdatePartnerCompanyPayload,
): Promise<PartnerCompany> {
  const response = await apiClient.patch<PartnerCompany>(
    `${BASE_PATH}/${id}`,
    payload,
  );
  return response.data;
}

/**
 * Search partner companies for dropdown (lightweight)
 * GET /admin/partner-companies/search
 *
 * @param params - Search query parameters
 * @returns Array of partner company options
 */
export async function searchPartnerCompanies(
  params: PartnerCompanySearchParams,
): Promise<PartnerCompanyOption[]> {
  const response = await apiClient.get<PartnerCompanyOption[]>(
    `${BASE_PATH}/search`,
    { params },
  );
  return response.data;
}

// =============================================================================
// User Assignment API Methods (T038 - US2)
// =============================================================================

/**
 * List users assigned to a partner company
 * GET /admin/partner-companies/:id/users
 *
 * @param companyId - Partner company ID
 * @param params - Query parameters (search, role, page, limit)
 * @returns Paginated list of assigned users
 */
export async function getPartnerCompanyUsers(
  companyId: string,
  params?: PartnerCompanyUserListParams,
): Promise<PaginatedResponse<AssignedUser>> {
  const response = await apiClient.get<PaginatedResponse<AssignedUser>>(
    `${BASE_PATH}/${companyId}/users`,
    { params },
  );
  return response.data;
}

/**
 * Assign user to partner company
 * POST /admin/partner-companies/:id/users
 *
 * @param companyId - Partner company ID
 * @param userId - User ID to assign
 * @returns Assignment result with previous company ID if user was moved
 */
export async function assignUserToPartnerCompany(
  companyId: string,
  userId: string,
): Promise<UserAssignmentResult> {
  const response = await apiClient.post<UserAssignmentResult>(
    `${BASE_PATH}/${companyId}/users`,
    { userId },
  );
  return response.data;
}

/**
 * Remove user from partner company
 * DELETE /admin/partner-companies/:id/users/:userId
 *
 * @param companyId - Partner company ID
 * @param userId - User ID to remove
 */
export async function removeUserFromPartnerCompany(
  companyId: string,
  userId: string,
): Promise<void> {
  await apiClient.delete(`${BASE_PATH}/${companyId}/users/${userId}`);
}

/**
 * Get partner company by slug (public, used for company login routes)
 * GET /partner-companies/slug/:slug
 */
export async function getPartnerCompanyBySlug(
  slug: string,
): Promise<PartnerCompany> {
  const response = await apiClient.get<PartnerCompany>(
    `/partner-companies/slug/${slug}`,
  );
  return response.data;
}

export const partnerCompaniesApi = {
  getPartnerCompanies,
  createPartnerCompany,
  getPartnerCompany,
  getPartnerCompanyBySlug,
  updatePartnerCompany,
  searchPartnerCompanies,
  getPartnerCompanyUsers,
  assignUserToPartnerCompany,
  removeUserFromPartnerCompany,
};
